/**
 * changelog-integrations.ts
 *
 * Fetches activity events from all supported integrations and normalises them
 * into a unified ChangelogIntegrationEvent array for use in changelog generation
 * and AI enrichment.
 *
 * Uses the EXACT same service classes and fetch patterns as each ActivityView
 * component so results are identical.
 */

import { BrowserJiraService } from './jira-service';
import { BrowserLinearService } from './linear-service';
import { BrowserMondayService } from './monday-service';
import { BrowserTrelloService } from './trello-service';
import { createConfluenceService } from './confluence-service';
import { createNotionService } from './notion-service';
import { createGoogleDocsService } from './google-docs-service';
import { atlassianOAuth2 } from './atlassian-oauth2';
import { linearOAuth2 } from './linear-oauth2';
import { mondayOAuth2 } from './monday-oauth2';
import type { ChangelogIntegrationConfigs, ChangelogIntegrationEvent, ChangelogIntegrationSource } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function lookbackDays(from: Date, to: Date): number {
  return Math.max(1, Math.ceil((to.getTime() - from.getTime()) / 86_400_000));
}

// ─── Source fetchers ──────────────────────────────────────────────────────────

async function fetchJiraEvents(
  cfg: NonNullable<ChangelogIntegrationConfigs['jira']>,
  from: Date,
  to: Date,
): Promise<ChangelogIntegrationEvent[]> {
  // Prefer Atlassian OAuth2 — same token covers Jira + Confluence
  const oauthToken = await atlassianOAuth2.getAccessToken().catch(() => null);
  const atlState = await atlassianOAuth2.getState().catch(() => null);

  const svc = new BrowserJiraService({
    domain: cfg.domain || atlState?.token?.cloudName || '',
    email: cfg.email || '',
    apiToken: cfg.apiToken || '',
    // When OAuth is available BrowserJiraService uses Bearer auth to
    // api.atlassian.com/ex/jira/{cloudId} and auto-falls back to Basic on 401
    ...(oauthToken ? { oauthToken, cloudId: atlState?.token?.cloudId } : {}),
  });

  const projectKey = cfg.projectKey?.trim() || '';
  if (!projectKey) {
    console.warn('[changelog-integrations] Jira projectKey not configured — skipping Jira events');
    return [];
  }
  const days = lookbackDays(from, to);
  const startDate = from.toISOString().split('T')[0];
  const endDate = to.toISOString().split('T')[0];
  const feed = await svc.getActivityFeed(projectKey, days, undefined, startDate, endDate);

  return feed.events
    .filter(e => {
      const ts = new Date(e.timestamp).getTime();
      return ts >= from.getTime() && ts <= to.getTime();
    })
    .map(e => {
      let description = e.type.replace(/_/g, ' ');
      if (e.type === 'status_change' && e.fromStatus && e.toStatus) {
        description = `Status: ${e.fromStatus} → ${e.toStatus}`;
      } else if (e.type === 'assignment' && e.toAssignee) {
        description = `Assigned to ${e.toAssignee}`;
      } else if (e.type === 'created') {
        description = `Issue created`;
      } else if (e.type === 'resolved') {
        description = `Issue resolved`;
      } else if (e.type === 'comment') {
        description = `Comment added`;
      } else if (e.fieldName) {
        description = `${e.fieldName}: ${e.fromValue ?? '—'} → ${e.toValue ?? '—'}`;
      }

      return {
        id: e.id,
        timestamp: e.timestamp,
        source: 'jira' as const,
        type: e.type,
        title: `${e.issueKey}: ${e.issueSummary}`,
        description,
        author: e.author.displayName,
      };
    });
}

async function fetchLinearEvents(
  cfg: NonNullable<ChangelogIntegrationConfigs['linear']>,
  from: Date,
  to: Date,
): Promise<ChangelogIntegrationEvent[]> {
  // Prefer Linear OAuth2 access token — falls back to PAT when not available
  const oauthToken = await linearOAuth2.getAccessToken().catch(() => null);

  const svc = new BrowserLinearService({
    apiKey: cfg.apiKey || '',
    // BrowserLinearService prefers accessToken over apiKey when both are provided
    ...(oauthToken ? { accessToken: oauthToken } : {}),
  });
  const days = lookbackDays(from, to);

  const events = await svc.getRecentActivity(undefined, days);

  return events
    .filter(e => {
      const ts = new Date(e.timestamp).getTime();
      return ts >= from.getTime() && ts <= to.getTime();
    })
    .map(e => {
      let description = e.type.replace(/_/g, ' ');
      if (e.fromState && e.toState) description = `Status: ${e.fromState} → ${e.toState}`;
      else if (e.fromAssignee || e.toAssignee) description = `Assigned to ${e.toAssignee ?? '—'}`;
      else if (e.fieldName) description = `${e.fieldName}: ${e.fromValue ?? '—'} → ${e.toValue ?? '—'}`;

      return {
        id: e.id,
        timestamp: e.timestamp,
        source: 'linear' as const,
        type: e.type,
        title: `${e.issueIdentifier}: ${e.issueSummary}`,
        description,
        author: e.author.displayName,
      };
    });
}

async function fetchMondayEvents(
  cfg: NonNullable<ChangelogIntegrationConfigs['monday']>,
  from: Date,
  to: Date,
): Promise<ChangelogIntegrationEvent[]> {
  // Prefer Monday.com OAuth2 access token — falls back to API key when not available
  const oauthToken = await mondayOAuth2.getAccessToken().catch(() => null);

  const svc = new BrowserMondayService({
    apiKey: cfg.apiKey || '',
    // BrowserMondayService prefers accessToken over apiKey when both are provided
    ...(oauthToken ? { accessToken: oauthToken } : {}),
  });
  const days = lookbackDays(from, to);

  const events = await svc.getRecentActivity(cfg.boardId, days);

  return events
    .filter(e => {
      const ts = new Date(e.timestamp).getTime();
      return ts >= from.getTime() && ts <= to.getTime();
    })
    .map(e => {
      let description = e.type.replace(/_/g, ' ');
      if (e.fieldName) description = `${e.fieldName}: ${e.fromValue ?? '—'} → ${e.toValue ?? '—'}`;

      return {
        id: e.id,
        timestamp: e.timestamp,
        source: 'monday' as const,
        type: e.type,
        title: `${e.boardName} / ${e.itemName}`,
        description,
        author: e.author.displayName,
      };
    });
}

async function fetchTrelloEvents(
  cfg: NonNullable<ChangelogIntegrationConfigs['trello']>,
  from: Date,
  to: Date,
): Promise<ChangelogIntegrationEvent[]> {
  const svc = new BrowserTrelloService({ apiKey: cfg.apiKey, token: cfg.token });
  const days = lookbackDays(from, to);

  const events = await svc.getRecentActivity(cfg.boardId, days);

  return events
    .filter(e => {
      const ts = new Date(e.timestamp).getTime();
      return ts >= from.getTime() && ts <= to.getTime();
    })
    .map(e => {
      let description = e.type.replace(/([A-Z])/g, ' $1').trim();
      if (e.listBefore && e.listAfter) description = `Moved: ${e.listBefore} → ${e.listAfter}`;
      else if (e.fieldName) description = `${e.fieldName}: ${e.fromValue ?? '—'} → ${e.toValue ?? '—'}`;

      return {
        id: e.id,
        timestamp: e.timestamp,
        source: 'trello' as const,
        type: e.type,
        title: `${e.boardName} / ${e.cardName}`,
        description,
        author: e.author.displayName,
      };
    });
}

async function fetchConfluenceEvents(
  from: Date,
  to: Date,
): Promise<ChangelogIntegrationEvent[]> {
  const svc = await createConfluenceService();
  if (!svc) return [];

  const { spaces } = await svc.getSpaces().catch(() => ({ spaces: [] as any[] }));
  const results: ChangelogIntegrationEvent[] = [];

  for (const space of spaces) {
    const { pages } = await svc.getPagesModifiedInRange(
      space.key,
      from.toISOString(),
      to.toISOString(),
      200,
    ).catch(() => ({ pages: [] as any[] }));

    for (const p of pages as any[]) {
      results.push({
        id: p.id,
        timestamp: p.updatedAt,
        source: 'confluence' as const,
        type: 'page_updated',
        title: `${p.spaceName ?? space.name} / ${p.title}`,
        description: `Page updated (v${p.version ?? '?'})`,
        author: p.authorId,
        url: p.url,
      });
    }
  }

  return results;
}

async function fetchNotionEvents(
  from: Date,
  to: Date,
): Promise<ChangelogIntegrationEvent[]> {
  const svc = await createNotionService();
  if (!svc) return [];

  const { events } = await svc.getEvents(from, to);

  return events.map(p => ({
    id: p.id,
    timestamp: p.lastEditedTime,
    source: 'notion' as const,
    type: 'page_edited',
    title: p.title || 'Untitled',
    description: `Page last edited`,
    author: p.lastEditedBy?.name,
    url: p.url,
  }));
}

async function fetchGoogleDocsEvents(
  from: Date,
  to: Date,
): Promise<ChangelogIntegrationEvent[]> {
  const svc = await createGoogleDocsService();
  if (!svc) return [];

  const { events } = await svc.getEvents(from, to);

  return (events as any[]).map(doc => ({
    id: doc.id,
    timestamp: doc.timestamp ?? doc.modifiedTime,
    source: 'google-docs' as const,
    type: (doc.eventType as string) ?? 'updated',
    title: doc.title,
    description: doc.eventType === 'created' ? 'Document created' : 'Document edited',
    author: doc.authorName ?? doc.lastModifyingUser?.displayName,
    url: doc.url,
  }));
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface FetchIntegrationEventsOptions {
  configs: ChangelogIntegrationConfigs;
  /** Which sources to fetch. If omitted, fetches all that have credentials. */
  enabled: ChangelogIntegrationSource[];
  from: Date;
  to: Date;
  onProgress?: (msg: string) => void;
}

/**
 * Fetches and normalises events from all requested integration sources.
 * Errors from individual sources are caught and logged — they never abort others.
 */
export async function fetchIntegrationEvents(
  opts: FetchIntegrationEventsOptions,
): Promise<ChangelogIntegrationEvent[]> {
  const { configs, enabled, from, to, onProgress } = opts;
  const results: ChangelogIntegrationEvent[] = [];

  const run = async (
    source: ChangelogIntegrationSource,
    fn: () => Promise<ChangelogIntegrationEvent[]>,
  ) => {
    if (!enabled.includes(source)) return;
    onProgress?.(`Fetching ${source} events…`);
    try {
      const evts = await fn();
      results.push(...evts);
      onProgress?.(`${source}: ${evts.length} event${evts.length !== 1 ? 's' : ''}`);
    } catch (err) {
      console.warn(`[changelog-integrations] ${source} failed:`, err);
      onProgress?.(`${source}: failed (skipped)`);
    }
  };

  await run('jira', () => configs.jira
    ? fetchJiraEvents(configs.jira, from, to)
    : Promise.resolve([]));

  await run('linear', () => configs.linear
    ? fetchLinearEvents(configs.linear, from, to)
    : Promise.resolve([]));

  await run('monday', () => configs.monday
    ? fetchMondayEvents(configs.monday, from, to)
    : Promise.resolve([]));

  await run('trello', () => configs.trello
    ? fetchTrelloEvents(configs.trello, from, to)
    : Promise.resolve([]));

  await run('confluence', () => configs.confluence
    ? fetchConfluenceEvents(from, to)
    : Promise.resolve([]));

  await run('notion', () => configs.notion
    ? fetchNotionEvents(from, to)
    : Promise.resolve([]));

  await run('google-docs', () => configs.googleDocs
    ? fetchGoogleDocsEvents(from, to)
    : Promise.resolve([]));

  results.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return results;
}
